import { useCallback, useMemo, useState } from "react";
import { buildPermissionGraph } from "../permissionGraph";

export const usePermissionSelection = (groups, initialSelectedIds = []) => {
  const { permissionById, dependentsOf } = useMemo(
    () => buildPermissionGraph(groups),
    [groups],
  );

  const [selected, setSelected] = useState(() =>
    initialSelectedIds.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
  );

  const requiresOf = useCallback(
    (id) => permissionById[id]?.requires || [],
    [permissionById],
  );

  const unmetFor = useCallback(
    (id) => (selected[id] ? requiresOf(id).filter((r) => !selected[r]) : []),
    [selected, requiresOf],
  );

  const brokenFor = useCallback(
    (id) =>
      selected[id] ? [] : (dependentsOf[id] || []).filter((d) => selected[d]),
    [selected, dependentsOf],
  );

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: true };
    });
  }, []);

  const groupCount = useCallback(
    (group) =>
      group.permissions.reduce((n, p) => n + (selected[p.id] ? 1 : 0), 0),
    [selected],
  );

  const permissionGroupKey = useMemo(() => {
    const map = {};
    groups.forEach((group) => {
      group.permissions.forEach((permission) => {
        map[permission.id] = group.key;
      });
    });
    return map;
  }, [groups]);

  const warningGroupKeys = useMemo(() => {
    const keys = new Set();
    Object.keys(permissionById).forEach((idStr) => {
      const id = Number(idStr);
      const unmet = unmetFor(id);
      if (unmet.length === 0) return;
      keys.add(permissionGroupKey[id]);
      unmet.forEach((requiredId) => keys.add(permissionGroupKey[requiredId]));
    });
    return keys;
  }, [permissionById, permissionGroupKey, unmetFor]);

  const groupStatus = useCallback(
    (group) => {
      if (warningGroupKeys.has(group.key)) return "warn";
      return groupCount(group) === 0 ? "none" : "some";
    },
    [groupCount, warningGroupKeys],
  );

  const selectedIds = useMemo(
    () => Object.keys(selected).map(Number),
    [selected],
  );

  const hasDependencyWarning = useMemo(
    () => selectedIds.some((id) => unmetFor(id).length > 0),
    [selectedIds, unmetFor],
  );

  return {
    selected,
    toggle,
    unmetFor,
    brokenFor,
    groupCount,
    groupStatus,
    permissionById,
    selectedIds,
    selectedCount: selectedIds.length,
    hasDependencyWarning,
  };
};
